/* The Fitting Room — gallery and the buying panel's response to a choice.
   ------------------------------------------------------------------------
   Deliberately narrow. Symmetry's variant-picker.js owns selection, the URL
   and availability; this owns only what the panel *says* about the selection:
   the price, the saving, the button and which photo is showing. Keeping the
   two apart means a theme update can change how selection works without
   silently leaving the price behind. */

(function () {
  'use strict';

  function init(root) {
    if (root.__tcfrReady) return;
    root.__tcfrReady = true;

    var main = root.querySelector('[data-tcfr-main]');
    var priceEl = root.querySelector('[data-tcfr-price]');
    var wasEl = root.querySelector('[data-tcfr-was]');
    var saveEl = root.querySelector('[data-tcfr-save]');
    var idEl = root.querySelector('[data-tcfr-id]');
    var addEl = root.querySelector('[data-tcfr-add]');
    var ctaPrice = root.querySelector('[data-tcfr-cta-price]');

    var variants = [];
    var json = root.parentNode.querySelector('[data-tcfr-variants]') ||
               document.querySelector('[data-tcfr-variants]');
    if (json) {
      try {
        variants = JSON.parse(json.textContent);
      } catch (e) {
        // A malformed map must not take the Add to bag button down with it:
        // the form still posts the variant the page was rendered with.
        variants = [];
      }
    }

    /* ── the photo's own margin decides whether it may grow ───────
       On a desktop the frame is taller than square and the photo is sized
       by height, so a square one overflows the width and the frame trims
       6.5% off each side (see tc-fitting-room.css). A suit has a fifth of
       the frame white on either hand and loses nothing; a cap is shot to
       the edge and lost its brim. So each photo is measured as it loads —
       the bounding box of everything that is not the background colour, on
       a 64px copy — and one with less than 8% to spare on either side is
       fitted whole. The pictures come from this origin, so the canvas can
       be read back; if it ever cannot, the photo is left as it was. */
    var measured = {};
    function edgeToEdge(img) {
      try {
        var n = 64, canvas = document.createElement('canvas');
        canvas.width = n; canvas.height = n;
        var ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(img, 0, 0, n, n);
        var px = ctx.getImageData(0, 0, n, n).data;
        var at = function (x, y) { var i = (y * n + x) * 4; return [px[i], px[i + 1], px[i + 2]]; };
        var corners = [at(0, 0), at(n - 1, 0), at(0, n - 1), at(n - 1, n - 1)];
        // The background is the median of the corners, so an off-white or
        // cream ground reads as ground as well as pure white does.
        var bg = [0, 1, 2].map(function (k) {
          var v = corners.map(function (c) { return c[k]; }).sort(function (a, b) { return a - b; });
          return (v[1] + v[2]) / 2;
        });
        var minX = n, maxX = -1;
        for (var y = 0; y < n; y++) {
          for (var x = 0; x < n; x++) {
            var i = (y * n + x) * 4;
            if (px[i + 3] < 26) continue;
            var d = Math.max(Math.abs(px[i] - bg[0]), Math.abs(px[i + 1] - bg[1]), Math.abs(px[i + 2] - bg[2]));
            if (d <= 15) continue;
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
          }
        }
        if (maxX < 0) return false;
        return Math.min(minX, n - 1 - maxX) / n < 0.08;
      } catch (e) {
        return false;
      }
    }

    function fitWhole() {
      var img = main && main.querySelector('img');
      if (!img || !img.complete || !img.naturalWidth) return;
      var key = img.currentSrc || img.src;
      if (!(key in measured)) measured[key] = edgeToEdge(img);
      main.classList.toggle('tcfr__gal-main--whole', measured[key]);
    }

    if (main) {
      // One listener on the frame catches every photo the gallery puts in
      // it, since a load event does not bubble but does reach a capturing
      // ancestor.
      main.addEventListener('load', function (e) {
        if (e.target && e.target.tagName === 'IMG') fitWhole();
      }, true);
      fitWhole();
    }

    /* ── gallery ───────────────────────────────────────────────── */

    /* A click used to set the frame's src and wait. Every photograph was a
       fresh 1400px download from the CDN, made only once it was asked for,
       so the strip answered in anything from three-quarters of a second to
       nearly four (measured on the live page, 24 Sep 2026) and read as
       broken. Now the strip's photographs are fetched ahead, at the width
       the frame actually shows rather than 1400, and a click swaps in a
       picture that is already decoded. The one wait left is the first
       click on a phone that has just arrived, and hovering or touching a
       thumb starts its fetch before the click lands. */

    var dpr = Math.min(3, window.devicePixelRatio || 1);
    function frameWidth() {
      // The taller side as well: on a desktop the frame is taller than
      // square and a square photo, sized by height, is wider than it.
      var w = main ? Math.max(main.clientWidth, main.clientHeight) : 0;
      if (!w) return 1400;
      // To the hundred above, so the strip asks the CDN for the few sizes
      // it has already made rather than one per screen width.
      return Math.min(1400, Math.max(600, Math.ceil((w * dpr) / 100) * 100));
    }
    function sized(src) {
      return src ? src.replace(/(\?|&)width=\d+/, '$1width=' + frameWidth()) : src;
    }
    function fullOf(thumb) {
      return sized(thumb.getAttribute('data-full'));
    }

    var warmed = {};
    function warm(src) {
      if (!src) return Promise.resolve();
      if (!warmed[src]) {
        warmed[src] = new Promise(function (resolve) {
          var pic = new Image();
          pic.decoding = 'async';
          pic.onload = function () {
            resolve();
            // Decoded ahead as well, where the browser will. Never waited
            // on: decode() does not settle while the page is not being
            // drawn, and a swap held on it never happened at all.
            if (pic.decode) pic.decode().catch(function () {});
          };
          // A failed fetch is forgotten, so the next click tries again.
          pic.onerror = function () { delete warmed[src]; resolve(); };
          pic.src = src;
        });
      }
      return warmed[src];
    }

    var wanted = '';
    function showImage(src, thumb) {
      if (!main || !src) return;
      src = sized(src);
      wanted = src;
      // The strip answers at once; the picture follows as soon as it is
      // ready, which is at once for anything already warmed.
      if (thumb) {
        root.querySelectorAll('[data-tcfr-thumb]').forEach(function (t) {
          t.setAttribute('aria-current', t === thumb ? 'true' : 'false');
        });
      }
      warm(src).then(function () {
        // A quicker click has overtaken this one.
        if (wanted !== src) return;
        var img = main.querySelector('img');
        if (!img) return;
        // srcset would otherwise win over the src we just set and put the
        // old photo straight back.
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.src = src;
      });
    }

    root.addEventListener('click', function (e) {
      var thumb = e.target.closest('[data-tcfr-thumb]');
      if (!thumb || !root.contains(thumb)) return;
      e.preventDefault();
      showImage(fullOf(thumb), thumb);
    });

    /* Intent comes before the click: a pointer over a thumb, a finger on
       it or focus reaching it starts the fetch a beat early. */
    function warmOnIntent(e) {
      var thumb = e.target && e.target.closest && e.target.closest('[data-tcfr-thumb]');
      if (thumb && root.contains(thumb)) warm(fullOf(thumb));
    }
    root.addEventListener('pointerover', warmOnIntent);
    root.addEventListener('focusin', warmOnIntent);
    root.addEventListener('touchstart', warmOnIntent, { passive: true });

    /* Ahead of any intent: the neighbours of the picture on screen first,
       then the rest of the colour on show, two at a time so the page's
       own loading is not elbowed aside, and only once the page has
       finished loading. Not on a connection that asked for less. */
    var saveData = !!(navigator.connection && navigator.connection.saveData);
    var stripReady = false;
    var warmingStrip = 0;
    function warmStrip() {
      if (saveData || !stripReady || warmingStrip) return;
      var list = visibleThumbs();
      var at = list.findIndex(function (t) { return t.getAttribute('aria-current') === 'true'; });
      if (at < 0) at = 0;
      var order = [];
      for (var d = 1; d < list.length; d++) {
        if (list[at + d]) order.push(list[at + d]);
        if (list[at - d]) order.push(list[at - d]);
      }
      var i = 0;
      function next() {
        var t = order[i++];
        if (!t) { warmingStrip--; return; }
        warm(fullOf(t)).then(next);
      }
      warmingStrip = 2;
      next();
      next();
    }
    (function warmSoon() {
      var go = function () {
        stripReady = true;
        warmStrip();
      };
      var idle = function () {
        if (window.requestIdleCallback) window.requestIdleCallback(go, { timeout: 2500 });
        else setTimeout(go, 1000);
      };
      /* After the page's own load, or three seconds in, whichever is
         first: the third-party widgets can hold the load event back for
         much longer than a customer waits before tapping a thumb. */
      var started = false;
      var once = function () { if (!started) { started = true; idle(); } };
      if (document.readyState === 'complete') once();
      else {
        window.addEventListener('load', once);
        setTimeout(once, 3000);
      }
    })();

    /* ── the strip follows the colour ──────────────────────────────
       A product in four cloths carries thirty photographs. Showing all of
       them means a customer looking at the wine boots is scrolling past
       navy ones to find another wine one; showing the first ten, which is
       what this did, meant the last two colours had no photographs at all.
       Each thumb knows its colour, so the strip can be the colour on
       screen and nothing else. */

    var strip = root.querySelector('[data-tcfr-strip]');

    function thumbs() {
      return [].slice.call(root.querySelectorAll('[data-tcfr-thumb]'));
    }

    function visibleThumbs() {
      return thumbs().filter(function (t) { return !t.hidden; });
    }

    /** The colour the picker is on, or '' when the product has no colours. */
    function chosenColour() {
      var groups = [].slice.call(root.querySelectorAll('.option-selector'));
      for (var i = 0; i < groups.length; i++) {
        var label = (groups[i].textContent || '').toLowerCase();
        if (label.indexOf('colour') === -1 && label.indexOf('color') === -1) continue;
        var picked = groups[i].querySelector('input:checked');
        if (picked) return picked.value;
      }
      return '';
    }

    function syncStrip() {
      if (!strip) return;
      var want = chosenColour();
      var all = thumbs();
      if (!want || !all.some(function (t) { return t.getAttribute('data-colour') === want; })) {
        /* No colour option, or none of the photographs claim this one - show
           everything rather than an empty strip, which is the worse failure. */
        all.forEach(function (t) { t.hidden = false; });
        warmStrip();
        return;
      }
      all.forEach(function (t) {
        t.hidden = t.getAttribute('data-colour') !== want;
      });

      /* If the picture on screen belongs to a colour nobody is looking at any
         more, step to the first one that is. */
      var current = all.filter(function (t) { return t.getAttribute('aria-current') === 'true'; })[0];
      if (!current || current.hidden) {
        var first = visibleThumbs()[0];
        if (first) showImage(fullOf(first), first);
      }
      if (strip.scrollTo) strip.scrollTo({ left: 0 });
      warmStrip();
    }

    /* ── arrows ────────────────────────────────────────────────────
       They step through what is on screen, not through everything, so
       next from the last wine photograph does not land on a navy one. */

    function step(by) {
      var list = visibleThumbs();
      if (list.length < 2) return;
      var at = list.findIndex(function (t) { return t.getAttribute('aria-current') === 'true'; });
      if (at < 0) at = 0;
      var next = list[(at + by + list.length) % list.length];
      showImage(fullOf(next), next);
      if (next.scrollIntoView) next.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }

    var prev = root.querySelector('[data-tcfr-prev]');
    var next = root.querySelector('[data-tcfr-next]');
    if (prev) prev.addEventListener('click', function (e) { e.preventDefault(); step(-1); });
    if (next) next.addEventListener('click', function (e) { e.preventDefault(); step(1); });

    /* The colour is changed by the theme's own picker, which we do not own -
       so this listens for the change rather than being told about it. */
    root.addEventListener('change', function () { setTimeout(syncStrip, 0); });
    syncStrip();

    /* ── zoom ──────────────────────────────────────────────────────
       Buying a suit online is buying cloth you cannot touch, and the
       weave is the whole difference between two brown checks. The
       gallery is a 600px square; the photograph behind it is far
       larger, so zoom costs nothing but the willingness to show it. */

    var lightbox = null;

    function closeZoom() {
      if (!lightbox) return;
      lightbox.remove();
      lightbox = null;
      document.removeEventListener('keydown', onZoomKey);
      document.body.style.overflow = '';
    }

    function onZoomKey(e) {
      if (e.key === 'Escape') closeZoom();
      else if (e.key === 'ArrowLeft') { e.preventDefault(); zoomStep(-1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); zoomStep(1); }
    }

    /* Stepping while zoomed moves the gallery underneath and shows the
       photograph it lands on, so closing the overlay finds the page where
       the customer left it rather than back on the first picture. The
       magnification is kept: somebody comparing two weaves up close does
       not want to zoom in again for the second. */
    function zoomStep(by) {
      if (!lightbox) return;
      step(by);
      // From the thumb the step landed on, not from the frame: the frame
      // swaps a moment later, once the picture is decoded.
      var current = visibleThumbs().filter(function (t) { return t.getAttribute('aria-current') === 'true'; })[0];
      var src = current ? current.getAttribute('data-full') : null;
      var img = lightbox.querySelector('.tcfr-zoom__img');
      if (src && img) img.src = src.replace(/(\?|&)width=\d+/, '$1width=2400');
    }

    function openZoom(src) {
      if (!src) return;
      closeZoom();

      lightbox = document.createElement('div');
      lightbox.className = 'tcfr-zoom';

      var img = document.createElement('img');
      img.className = 'tcfr-zoom__img';
      // The largest Shopify will serve, not the 1400 the page is using:
      // the point of opening it is to see more than was already visible.
      img.src = src.replace(/(\?|&)width=\d+/, '$1width=2400');
      img.alt = '';
      lightbox.appendChild(img);

      var close = document.createElement('button');
      close.className = 'tcfr-zoom__close';
      close.type = 'button';
      close.setAttribute('aria-label', 'Close');
      close.textContent = '×';
      lightbox.appendChild(close);

      // Arrows, the same two the gallery has, so the next photograph is a
      // click away instead of close-and-reopen. Left out when there is
      // only one picture to see.
      if (visibleThumbs().length > 1) {
        [['prev', -1, 'm15 5-7 7 7 7', 'Previous image'], ['next', 1, 'm9 5 7 7-7 7', 'Next image']].forEach(function (a) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'tcfr-zoom__arrow tcfr-zoom__arrow--' + a[0];
          b.setAttribute('aria-label', a[3]);
          b.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="' + a[2] + '"/></svg>';
          b.addEventListener('click', function (e) { e.stopPropagation(); zoomStep(a[1]); });
          lightbox.appendChild(b);
        });
      }

      // Anywhere outside the photograph closes it, which is what everyone
      // tries first.
      lightbox.addEventListener('click', function (e) {
        if (e.target !== img) closeZoom();
      });

      // A swipe steps as well, on a phone. Not while the picture is at its
      // largest, when a drag is how the customer moves around the cloth.
      var touch = null;
      lightbox.addEventListener('touchstart', function (e) {
        if (e.touches.length !== 1) { touch = null; return; }
        touch = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }, { passive: true });
      lightbox.addEventListener('touchend', function (e) {
        if (!touch || lightbox.classList.contains('is-max')) return;
        var t = e.changedTouches[0];
        var dx = t.clientX - touch.x, dy = t.clientY - touch.y;
        touch = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) zoomStep(dx < 0 ? 1 : -1);
      }, { passive: true });

      // Click the photograph itself to step between the two magnifications,
      // rather than needing a control to do it.
      img.addEventListener('click', function () {
        lightbox.classList.toggle('is-max');
      });

      document.addEventListener('keydown', onZoomKey);
      // The page behind must not scroll while the overlay is up.
      document.body.style.overflow = 'hidden';
      document.body.appendChild(lightbox);
    }

    /* The arrows sit on top of the photograph, so a click on one reaches the
       frame as well. Stepping through the gallery is not a request to open it
       full size - only a click on the photograph itself is. */
    function onArrow(e) {
      return !!(e.target.closest && e.target.closest('[data-tcfr-prev],[data-tcfr-next]'));
    }

    if (main) {
      main.addEventListener('click', function (e) {
        if (onArrow(e)) return;
        var img = main.querySelector('img');
        if (img) openZoom(img.currentSrc || img.src);
      });
      main.setAttribute('role', 'button');
      main.setAttribute('tabindex', '0');
      main.setAttribute('aria-label', 'Zoom');
      main.addEventListener('keydown', function (e) {
        // The arrow keys page through the gallery when the frame has focus.
        // Not while the overlay is up: the frame keeps focus behind it, and
        // the same key reaching both handlers stepped twice — 1, 3, 1, 3.
        if (lightbox) return;
        if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1); return; }
        if (e.key === 'ArrowRight') { e.preventDefault(); step(1); return; }
        if (e.key !== 'Enter' && e.key !== ' ') return;
        if (onArrow(e)) return;
        e.preventDefault();
        var img = main.querySelector('img');
        if (img) openZoom(img.currentSrc || img.src);
      });
    }

    /* ── what the panel says about the choice ──────────────────── */

    /** The chosen value of every option, in the order the variants list them. */
    function chosen() {
      var selectors = Array.prototype.slice.call(
        root.querySelectorAll('.option-selector')
      );
      selectors.sort(function (a, b) {
        return (+a.getAttribute('data-index') || 0) - (+b.getAttribute('data-index') || 0);
      });

      return selectors.map(function (sel) {
        var radio = sel.querySelector('input[type="radio"]:checked');
        if (radio) return radio.value;
        var select = sel.querySelector('select');
        if (select) return select.value;
        // The dropdown style keeps its answer in a hidden input rather than
        // a native select.
        var hidden = sel.querySelector('input[type="hidden"]');
        return hidden ? hidden.value : null;
      });
    }

    function match(values) {
      if (!variants.length || !values.length) return null;
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        if (!v.options || v.options.length !== values.length) continue;
        var same = true;
        for (var j = 0; j < values.length; j++) {
          if (values[j] === null) { same = false; break; }
          if (String(v.options[j]) !== String(values[j])) { same = false; break; }
        }
        if (same) return v;
      }
      return null;
    }

    /* ── the star rating jumps to the reviews ─────────────────────
       Okendo's rating scrolls to its own widget, which works until the
       widget is not there: on a phone the full list is display:none because
       the carousel stands in for it, and an element with no box has no
       position to scroll to — so the tap did nothing at all.

       Caught in the capture phase, before Okendo's own handler, and only
       when its target is genuinely hidden. On a desktop this does not fire
       and Okendo behaves exactly as it always has. */
    var starsEl = root.querySelector('.tcfr__stars');
    if (starsEl) {
      starsEl.addEventListener(
        'click',
        function (e) {
          var band = document.getElementById('tcfr-reviews');
          if (!band) return;
          var full = band.querySelector('.tcfr-reviews__full');
          if (full && getComputedStyle(full).display !== 'none') return;

          e.preventDefault();
          e.stopPropagation();

          /* Land under the sticky header, not behind it.
             scrollIntoView puts the card's top edge at the top of the
             viewport, where the site header — 69px of it, and fixed — covers
             the "Reviews" heading and the first slide. The reader arrives
             mid-review with nothing naming what they are looking at. The
             header's real height is measured rather than assumed, since it
             differs between phone and desktop, and the pinned element is the
             header group's own section wrapper rather than any <header> tag —
             this theme has several of those, including one inside the mobile
             menu drawer that is not pinned to anything.

             Instant, not smooth: measured on this page, a smooth
             scrollIntoView moves nothing at all while the default lands
             3,117px down. Something in the theme's scroll setup swallows it,
             and a jump that works beats an animation that does not. */
          var clear = 0;
          var bars = document.querySelectorAll('.shopify-section-group-header-group');
          for (var i = 0; i < bars.length; i++) {
            var pos = getComputedStyle(bars[i]).position;
            /* Only the sticky bars, and their own height rather than where
               they happen to sit now. At the top of the page the header rests
               below the announcement bar, 42px down, so its current position
               says nothing about what it will cover once the page has moved
               and it pins itself to the top. The announcement bar is static —
               it scrolls away and covers nothing, so it is not counted. */
            if (pos !== 'sticky' && pos !== 'fixed') continue;
            clear += bars[i].offsetHeight;
          }
          var y = band.getBoundingClientRect().top + window.scrollY - clear - 16;
          window.scrollTo(0, Math.max(0, y));
        },
        true
      );
    }

    var skuEl = root.querySelector('[data-tcfr-sku]');
    var stockEl = root.querySelector('[data-tcfr-stock]');

    /*
     * On a suit sold as its three pieces, drop the size off the end of the SKU.
     *
     * The pieces block replaces the suit's own size buttons, so the suit
     * variant never changes and the size on the end of its code is whatever
     * the default happened to be — "Madrid_suit_Navy_34" on a page where
     * somebody is buying a 40 blazer and 36 trousers. It is not stale, it is
     * wrong, and a customer reading it out on the phone would be quoting a
     * garment nobody is buying.
     *
     * The stem is the part that identifies the suit, which is what the code is
     * for; the size was only ever true by accident. Only the trailing
     * _<digits> goes, so a code that never carried one is left alone.
     */
    var sellsPieces = !!document.querySelector('.tcsep');
    function shownSku(sku) {
      return sellsPieces ? String(sku || '').replace(/_\d+$/, '') : sku;
    }

    /* The first one came from Liquid, which does not know about any of this. */
    if (skuEl && sellsPieces) {
      var firstSku = skuEl.querySelector('b');
      if (firstSku) firstSku.textContent = shownSku(firstSku.textContent.trim());
    }

    /* How many of this exact size there are.
       The count rather than "In stock", because a customer dressing a
       wedding party is not asking whether they can buy one — they are asking
       whether they can buy five in a 42, and "In stock" does not answer that.

       Only ever stated when Shopify is actually tracking the variant: an
       untracked one reports zero, and a count of nothing under a buyable size
       is worse than saying nothing at all. */
    function stockLine(v) {
      if (!v.available) return { text: t('out_of_stock'), level: 'none' };
      if (!v.tracked) return { text: t('in_stock'), level: 'ok' };
      // Available but nothing counted: the shop is set to keep selling it.
      if (v.qty <= 0) return { text: t('to_order'), level: 'ok' };
      // Low enough to change what someone does next, so it is coloured.
      if (v.qty <= 3) return { text: t('only_left', v.qty), level: 'low' };
      /* Exact up to ten, then capped.
         Ten covers the largest party this shop sees, so the number is still
         precise everywhere it decides an order. Past that the exact figure
         tells the customer nothing they need and tells a competitor reading
         the page our whole stock position. */
      if (v.qty > 10) return { text: '10+ in stock', level: 'ok' };
      return { text: t('qty_in_stock', v.qty), level: 'ok' };
    }

    /* ── back in stock ────────────────────────────────────────────
       Replaces the button rather than sitting under it: a disabled "Sold
       out" is a dead end, and the useful thing to offer at that exact
       moment is to be told when it returns. Subscribed against the variant,
       so the customer hears when *their* size is back — being emailed about
       a 38" when you wanted a 44" is worse than not being emailed. */

    var bis = root.querySelector('[data-tcfr-bis]');
    var bisMsg = root.querySelector('[data-tcfr-bis-msg]');

    function bisSay(text, ok) {
      if (!bisMsg) return;
      bisMsg.textContent = text || '';
      bisMsg.hidden = !text;
      bisMsg.setAttribute('data-state', ok ? 'ok' : 'bad');
    }

    if (bis) {
      bis.addEventListener('submit', function (e) {
        e.preventDefault();
        var endpoint = cfg().bisEndpoint;
        if (!endpoint) return;

        var field = bis.querySelector('input[type="email"]');
        var email = (field && field.value) || '';
        var variantId = idEl && idEl.value;
        if (!variantId) return;

        var go = bis.querySelector('.tcfr__bis-go');
        if (go) { go.disabled = true; go.textContent = t('sending'); }
        bisSay('', true);

        fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ email: email.trim(), variantId: String(variantId) }),
        })
          .then(function (r) {
            return r.json().then(function (body) {
              // As with the cart: a 4xx does not reject, so it is checked.
              if (!r.ok) throw new Error((body && body.error) || t('try_again'));
              return body;
            });
          })
          .then(function () {
            bis.classList.add('is-done');
            bisSay("Done — we'll email you the moment this size is back.", true);
            // Somebody who has just asked to be told something is the one
            // person with a reason to say yes to notifications. tc-web-push
            // listens for this and offers; if it is not loaded, nothing
            // happens and the email still goes.
            document.dispatchEvent(new CustomEvent('tc:stock-request'));
          })
          .catch(function (err) {
            bisSay(err.message || t('did_not_send'), false);
          })
          .then(function () {
            if (go) { go.disabled = false; go.textContent = t('notify_me'); }
          });
      });
    }

    function apply(v) {
      if (!v) return;

      if (skuEl) {
        var sku = v.sku || '';
        skuEl.hidden = !sku;
        var b = skuEl.querySelector('b');
        if (b) b.textContent = shownSku(sku);
      }

      if (stockEl) {
        var s = stockLine(v);
        stockEl.textContent = s.text;
        stockEl.setAttribute('data-level', s.level);
      }

      // The cut-off promise cannot stand over a size that cannot be ordered.
      var cutoffEl = document.querySelector('[data-tcfr-cutoff]');
      if (cutoffEl && cutoffEl.textContent.trim()) cutoffEl.hidden = !v.available;

      if (idEl) idEl.value = v.id;
      if (priceEl) priceEl.textContent = v.price;
      if (ctaPrice) ctaPrice.textContent = v.price;

      /*
       * A was-price the section worked out from the suit's pieces is not the
       * variant's to overwrite.
       *
       * On a suit sold as pieces the comparison is the sum of what the pieces
       * cost separately, which no single variant knows — so a variant carrying
       * no compare_at of its own would blank a figure that is correct.
       */
      if (wasEl && !wasEl.hasAttribute('data-tcfr-was-fixed')) {
        wasEl.textContent = v.onSale ? v.compare : '';
        wasEl.hidden = !v.onSale;
        if (saveEl) {
          saveEl.textContent = v.onSale ? v.saved + '% off' : '';
          saveEl.hidden = !v.onSale;
        }
      }

      /* The sold-out state swaps the control instead of greying it out. */
      if (bis) {
        var wasHidden = bis.hidden;
        bis.hidden = v.available;
        if (!v.available && wasHidden) {
          bis.classList.remove('is-done');
          bisSay('', true);
        }
      }
      var form = addEl && addEl.closest('form');
      if (form) form.hidden = !v.available;

      if (addEl) {
        addEl.disabled = !v.available;
        if (!v.available) {
          addEl.textContent = t('sold_out');
        } else if (!addEl.querySelector('[data-tcfr-cta-price]')) {
          // Restored after a sold-out variant replaced the button's contents.
          // The theme's own label first — it is already translated where the
          // merchant translated the theme — and ours only when there isn't one.
          addEl.textContent = (addEl.getAttribute('data-label') || t('add_to_bag')) + ' — ';
          var span = document.createElement('span');
          span.setAttribute('data-tcfr-cta-price', '');
          span.textContent = v.price;
          addEl.appendChild(span);
          ctaPrice = span;
        }
      }

      if (v.image) {
        // The same path as a click, so the strip's own copy is used when
        // the variant's photograph is one of its thumbs.
        var own = thumbs().filter(function (t) { return sized(t.getAttribute('data-full')) === sized(v.image); })[0] || null;
        showImage(v.image, own);
      }
    }

    if (addEl) {
      addEl.setAttribute(
        'data-label',
        (addEl.textContent.split('—')[0] || t('add_to_bag')).trim()
      );
    }

    // The size the page loaded with also needs its stock line — it is written
    // by script, so without this it is blank until something is clicked.
    apply(match(chosen()));

    root.addEventListener('change', function (e) {
      if (!e.target.closest('.option-selector')) return;
      apply(match(chosen()));
    });

    // The dropdown style commits its choice without firing `change` on
    // anything this listener can see, so the picker's own event is used
    // where the theme provides one.
    document.addEventListener('variant:change', function (e) {
      var v = e && e.detail && e.detail.variant;
      if (v && v.id) apply(match(chosen()) || null);
    });
  }

  /* ── reviews, shortened on a phone ────────────────────────────────
     Okendo renders every review it has fetched, one under another. On a
     desktop that column is a reasonable page; on a phone it is several
     screens of scrolling between the outfit and everything below it, and
     the customer has usually decided by the third one.

     Three, then a button. Deliberately built against one Okendo class —
     the repeated row — and it does nothing at all if that class is not
     found, so a change on their side leaves the page exactly as it is now
     rather than breaking it. */

  var REVIEW_ITEM = '.oke-w-reviews-list-item';
  var SHOWN = 3;

  function clampReviews() {
    var band = document.getElementById('tcfr-reviews');
    if (!band || band.__tcfrClamped) return false;

    // Desktop has the room; this is a phone-only problem.
    if (!window.matchMedia('(max-width: 749px)').matches) return false;

    var items = band.querySelectorAll(REVIEW_ITEM);
    if (items.length <= SHOWN) return false;

    band.__tcfrClamped = true;
    band.classList.add('tcfr-reviews--clamped');

    var more = document.createElement('button');
    more.type = 'button';
    more.className = 'tcfr-reviews__more';
    more.textContent = t('show_all_reviews', items.length);
    more.addEventListener('click', function () {
      band.classList.remove('tcfr-reviews--clamped');
      more.remove();
    });

    var list = items[0].parentElement;
    (list.parentElement || band).insertBefore(more, list.nextSibling);
    return true;
  }

  function watchReviews() {
    if (clampReviews()) return;
    var band = document.getElementById('tcfr-reviews');
    if (!band) return;

    // The widget arrives well after this script does, and grows as more
    // reviews load, so the DOM is watched rather than polled on a guess.
    var observer = new MutationObserver(function () {
      if (clampReviews()) observer.disconnect();
    });
    observer.observe(band, { childList: true, subtree: true });

    // Nothing to clamp after half a minute means the widget is empty, absent,
    // or Okendo has renamed the class. Either way, stop watching.
    setTimeout(function () {
      observer.disconnect();
    }, 30000);
  }

  /* ── delivery, for wherever the customer is ───────────────────────
     The options come from Shopify's own delivery profiles, so the page and
     the checkout can never disagree. Rendered here rather than in Liquid
     because the answer depends on the market the storefront resolved, and
     the countdown has to keep ticking. */

  /**
   * Everything this script says out loud, in English — the floor.
   *
   * The other languages used to be written here too, which made this file a
   * second translation table nobody else could see or edit. They now come in
   * with the section's config, from the theme's locale files, which are
   * filled from the one table the apps and the admin panel share; see
   * pickLocale below. English stays here so a key missing there still reads.
   *
   * `%s` marks a substitution. Word order moves between languages — German
   * puts the day before the service — so each sentence is written whole rather
   * than glued together from fragments at the call site.
   */
  var T = {
    en: {
      out_of_stock: 'Out of stock', in_stock: 'In stock', to_order: 'Available to order',
      only_left: 'Only %s left', qty_in_stock: '%s in stock', sold_out: 'Sold out',
      add_to_bag: 'Add to bag', sending: 'Sending…', notify_me: 'Notify me',
      try_again: 'Please try again', did_not_send: 'That did not send. Please try again.',
      show_all_reviews: 'Show all %s reviews',
      delivery_to: 'Delivery to %s', free_over: 'free over %s', free: 'Free', from: 'from %s',
      next_day: 'next-day delivery', is_free: ' (free)',
      order_within: 'Order within <b>%s</b> with <b>%s</b>%s to get it %s',
      order_before: 'Order before <b>%s %s</b> with <b>%s</b>%s to get it %s',
      today: 'today', tomorrow: 'tomorrow', on_day: 'on %s',
      days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      promise_over: 'Free delivery over %s · 30-day returns', promise_returns: '30-day returns',
      made_to_order: 'Production time is %s working days',
      made_to_order_detail: 'This one is made to order — cut and sewn for you from scratch, which takes %s working days before it leaves us. You will get an email the moment it ships.',
    },
  };

  var LOCALE = 'en';
  var WORDS = T.en;

  /** One string, with its substitutions filled left to right. */
  function t(key) {
    var out = WORDS[key] != null ? WORDS[key] : T.en[key];
    for (var i = 1; i < arguments.length; i++) {
      out = out.replace('%s', arguments[i]);
    }
    return out;
  }

  /** £50, €150 — and 150 €, which is how the other four write it. */
  /* The symbol a shopper reads, from the country whose money it is: £, €,
     zł, $. A hardcoded map of four had nothing for złoty, so a Polish page
     showed the figure with no symbol at all. */
  var CURRENCY_LOCALE = {
    GBP: 'en-GB', EUR: 'de-DE', PLN: 'pl-PL', USD: 'en-US', SGD: 'en-SG',
    CHF: 'de-CH', SEK: 'sv-SE', DKK: 'da-DK', NOK: 'nb-NO', CZK: 'cs-CZ',
    RON: 'ro-RO', HUF: 'hu-HU', AUD: 'en-AU', CAD: 'en-CA', NZD: 'en-NZ',
  };
  function shapeCurrency(value, currency, decimals) {
    var code = String(currency || 'GBP').toUpperCase();
    var locale = code === 'EUR' && LOCALE && LOCALE !== 'en' ? LOCALE : (CURRENCY_LOCALE[code] || 'en-GB');
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: code,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: decimals == null ? 2 : decimals,
        maximumFractionDigits: decimals == null ? 2 : decimals,
      }).format(value);
    } catch (e) {
      return code + ' ' + value.toFixed(decimals == null ? 2 : decimals);
    }
  }

  /* A threshold is a round number and reads better without the pennies:
     "free over £75", not "free over £75.00". */
  function thresholdLabel(amount, currency) {
    var n = parseFloat(amount);
    if (!isFinite(n)) return '';
    return shapeCurrency(n, currency, n % 1 === 0 ? 0 : 2);
  }

  function cfg() {
    try {
      var n = document.querySelector('[data-tcfr-config]');
      return n ? JSON.parse(n.textContent) || {} : {};
    } catch (e) {
      return {};
    }
  }

  /** The language the page is in, taken once from the section's config. */

  /* Shopify's translation filter HTML-escapes every value, which is right
     for text in the page and wrong for words handed to a script: "&" arrives
     as "&amp;" and a <b> as "&lt;b&gt;". Undone here, once, on the way in. */
  function unescapeLabels(obj) {
    var out = {};
    for (var k in obj) {
      if (!Object.prototype.hasOwnProperty.call(obj, k)) continue;
      var v = obj[k];
      out[k] = typeof v === 'string'
        ? v.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'")
        : v;
    }
    return out;
  }

  (function pickLocale() {
    var c = cfg();
    var code = (c.locale || 'en').slice(0, 2).toLowerCase();
    LOCALE = code;
    /* The page's words come from the theme's locale files (see the config
       block in the section), which locales-sync.js fills from the same table
       the apps and the admin panel use. Anything missing there, or still
       marked by Shopify as untranslated, keeps the English floor above. */
    var given = unescapeLabels(c.strings || {});
    var words = {};
    for (var k in T.en) if (Object.prototype.hasOwnProperty.call(T.en, k)) words[k] = T.en[k];
    for (var g in given) {
      if (!Object.prototype.hasOwnProperty.call(given, g)) continue;
      var v = given[g];
      if (typeof v !== 'string' || !v || v.indexOf('translation missing') === 0) continue;
      words[g] = g === 'days' ? v.split(',') : v;
    }
    WORDS = words;
  })();

  function priceLabel(o) {
    var amount = parseFloat(o.price);
    if (!isFinite(amount) || amount === 0) return t('free');
    var figure = shapeCurrency(amount, o.currency);
    return o.from ? t('from', figure) : figure;
  }

  /**
   * The next dispatch deadline, and what it buys.
   *
   * Read in London time explicitly: the cut-off is when the warehouse stops
   * packing, which does not move because the customer is in Madrid.
   *
   * Two facts, not one. The deadline may be today — in which case there is a
   * countdown worth running — or it may be the next working morning, which is
   * the honest answer on a Sunday and after 3:30pm. And the day it arrives is
   * the next *working* day after that deadline, which is why a Friday
   * afternoon order says Monday rather than "tomorrow".
   */
  /** Euros go after the figure everywhere but English. */
  function currencyFirst(currency) {
    return !(currency === 'EUR' && LOCALE !== 'en');
  }

  /**
   * The cut-off, on the clock the reader uses.
   *
   * 12-hour with am/pm in English; the other four count to 24 and "3:30pm"
   * reads as a typo in Berlin.
   */
  function clockWord(part) {
    if (LOCALE === 'en') {
      return (
        (part.hour % 12 || 12) +
        (part.minute ? ':' + String(part.minute).padStart(2, '0') : '') +
        (part.hour < 12 ? 'am' : 'pm')
      );
    }
    return part.hour + ':' + String(part.minute).padStart(2, '0');
  }

  function londonNow() {
    return new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/London' }));
  }

  /**
   * The days this warehouse actually dispatches, from the section setting.
   *
   * Not an assumption. Monday to Friday is the common case but not this
   * shop's: the carriers collect on Saturday and Sunday too, and a delivery
   * date worked out from the wrong calendar is a promise the customer will
   * hold us to. Whoever knows the rota sets it in the theme editor.
   */
  var DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

  function dispatchDays() {
    var raw = String(cfg().dispatchDays || 'Mon,Tue,Wed,Thu,Fri').toLowerCase();
    var set = {};
    raw.split(',').forEach(function (part) {
      var key = part.trim().slice(0, 3);
      var i = DAY_KEYS.indexOf(key);
      if (i > -1) set[i] = true;
    });
    // An unreadable setting must not stop the page saying anything sensible.
    return Object.keys(set).length ? set : { 1: true, 2: true, 3: true, 4: true, 5: true };
  }

  /**
   * The cut-off for a given day, which is not the same every day.
   *
   * Saturday's collection goes at noon and the weekday one at half three, so
   * a single shop-wide time would tell a Saturday afternoon shopper to hurry
   * for a van that left two hours ago. Written as "Mon-Fri 15:30; Sat 12:00;
   * Sun 15:30"; a bare "15:30" still means every day.
   */
  function cutoffParts(day) {
    var raw = String(cfg().cutoffTime || '15:30').trim();
    var fallback = { hour: 15, minute: 30 };
    var plain = raw.match(/^(\d{1,2}):(\d{2})$/);
    if (plain) return { hour: +plain[1], minute: +plain[2] };

    var want = typeof day === 'number' ? day : londonNow().getDay();
    var found = null;

    raw.split(';').forEach(function (chunk) {
      var m = chunk.trim().match(/^([A-Za-z,\-]+)\s+(\d{1,2}):(\d{2})$/);
      if (!m) return;
      var time = { hour: +m[2], minute: +m[3] };

      m[1].split(',').forEach(function (part) {
        var range = part.trim().toLowerCase().split('-');
        var from = DAY_KEYS.indexOf(range[0].slice(0, 3));
        if (from < 0) return;
        var to = range[1] ? DAY_KEYS.indexOf(range[1].slice(0, 3)) : from;
        if (to < 0) to = from;
        // Walk the range forwards so Fri-Mon wraps the way it reads.
        for (var i = from, guard = 0; guard < 7; guard++) {
          if (i === want) found = time;
          if (i === to) break;
          i = (i + 1) % 7;
        }
      });
    });

    return found || fallback;
  }

  /**
   * The bank holidays, as a set of YYYY-MM-DD keys.
   *
   * England and Wales only, which is where the warehouse is: the countdown is
   * about the day a parcel leaves us, not the day it is opened, so a Scottish
   * customer ordering on 2 January is told the truth about our dispatch.
   */
  var HOLIDAYS = null;
  function holidays() {
    if (!HOLIDAYS) {
      HOLIDAYS = {};
      var list = cfg().bankHolidays;
      if (Object.prototype.toString.call(list) === '[object Array]') {
        for (var i = 0; i < list.length; i++) HOLIDAYS[String(list[i])] = true;
      }
    }
    return HOLIDAYS;
  }

  /** A date as London writes it, which is the calendar the holidays are on. */
  function dateKey(d) {
    var p = function (n) { return (n < 10 ? '0' : '') + n; };
    return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate());
  }

  /*
   * A bank holiday is not a working day, whatever the weekday says.
   *
   * Every one of these falls on a Monday or a Friday except Christmas, so the
   * old rule — dispatch days by weekday alone — promised next-day delivery on
   * exactly the days nothing moves, and the customer read that promise on the
   * page. Boxing Day is the worst of them: a Monday in the middle of the one
   * week people most need the date to be right.
   */
  function isWorkingDay(d, days) {
    if (holidays()[dateKey(d)]) return false;
    return !!(days || dispatchDays())[d.getDay()];
  }

  function nextWorkingDay(from) {
    var days = dispatchDays();
    var d = new Date(from);
    var guard = 0;
    do {
      d.setDate(d.getDate() + 1);
    } while (!isWorkingDay(d, days) && ++guard < 14);
    return d;
  }

  /** How that date reads relative to today: "tomorrow", or its name. */
  function dayWord(target, today) {
    var a = new Date(today); a.setHours(0, 0, 0, 0);
    var b = new Date(target); b.setHours(0, 0, 0, 0);
    var days = Math.round((b - a) / 86400000);
    if (days === 0) return t('today');
    if (days === 1) return t('tomorrow');
    return WORDS.days[target.getDay()];
  }

  /**
   * "today" and "tomorrow" stand on their own; a weekday needs its preposition.
   * "to get it Thursday" reads like a telegram — "to get it on Thursday" is the
   * sentence a person would say.
   */
  function whenPhrase(word) {
    return (word === t('today') || word === t('tomorrow')) ? word : t('on_day', word);
  }

  function cutOff() {
    var now = londonNow();
    var t = cutoffParts(now.getDay());
    var today = new Date(now);
    today.setHours(t.hour, t.minute, 0, 0);

    // Still time to make today's van.
    if (isWorkingDay(now) && now < today) {
      var ms = today - now;
      return {
        live: true,
        hours: Math.floor(ms / 3600000),
        minutes: Math.floor((ms % 3600000) / 60000),
        arrives: dayWord(nextWorkingDay(today), now),
        arrivesDay: nextWorkingDay(today).getDay(),
      };
    }

    // Missed it: the next collection is the next dispatch day, at that day's
    // own cut-off rather than today's.
    var next = nextWorkingDay(now);
    var nt = cutoffParts(next.getDay());
    next.setHours(nt.hour, nt.minute, 0, 0);
    return {
      live: false,
      when: dayWord(next, now),
      clock: clockWord(nt),
      arrives: dayWord(nextWorkingDay(next), now),
      arrivesDay: nextWorkingDay(next).getDay(),
    };
  }

  /**
   * The sentence, in both places it is shown.
   *
   * It names the service and its price, and it has to. "Order before 3:30pm
   * tomorrow for delivery Tuesday" reads as a promise about the order, when
   * it is only true of the next-day service — on Standard the same order
   * arrives Wednesday or later. Stated as a date without its condition it is
   * a delivery promise the customer will hold us to and we would not have
   * made. Naming the price also makes clear it is a paid upgrade rather than
   * what they get by default.
   */
  function cutOffText(c, clock, service) {
    var name = service && service.name ? service.name : t('next_day');
    var cost = '';
    if (service) {
      var amount = parseFloat(service.price);
      cost = !isFinite(amount) || amount === 0 ? t('is_free') : ' (' + priceLabel(service) + ')';
    }

    if (c.live) {
      var left = (c.hours ? c.hours + 'h ' : '') + c.minutes + 'm';
      return t('order_within', left, name, cost, whenPhrase(c.arrives));
    }
    // Named rather than counted down: a deadline two days out is a fact, not
    // a race, and a ticking clock against it would be theatre.
    return t('order_before', clock, c.when, name, cost, whenPhrase(c.arrives));
  }

  function renderDelivery(host, data) {
    var options = (data && data.options) || [];
    host.textContent = '';
    if (!options.length) return;

    var country = host.getAttribute('data-country-name') || '';
    var head = document.createElement('p');
    head.className = 'tcfr-delivery__to';
    head.textContent = t('delivery_to', country);
    host.appendChild(head);

    // The cut-off countdown, UK only — it is a UK warehouse promise, and
    // showing "order within 2 hours" to someone in Australia is a lie.
    if ((host.getAttribute('data-country') || '') === 'GB') {
      /* The service that actually delivers on the day named.
         A Saturday order does not travel on Evri Next Day — it travels on
         the Sunday service, which is a different rate at a different price.
         Naming the weekday one beside a Sunday date would quote a price the
         customer will not be charged for a journey they are not buying. */
      var arrivesOn = null;
      var probe = cutOff();
      if (probe.arrivesDay === 0) arrivesOn = /sunday delivery/i;
      else if (probe.arrivesDay === 6) arrivesOn = /saturday delivery/i;

      var nextDay = arrivesOn
        ? options.filter(function (o) {
            return arrivesOn.test(o.estimate || '');
          })[0]
        : null;

      // Weekday arrivals, and anywhere the weekend service is not offered.
      if (!nextDay) {
        nextDay = options.filter(function (o) {
          return /next working day/i.test(o.estimate || '');
        })[0];
      }
      if (nextDay) {
        var c = cutOff();
        // A named deadline carries the clock of the day it falls on.
        var text = cutOffText(c, c.clock || clockWord(cutoffParts()), nextDay);

        var line = document.createElement('p');
        line.className = 'tcfr-delivery__cutoff';
        line.innerHTML = text;
        if (c.live) line.setAttribute('data-live', '1');
        host.appendChild(line);

        /* The same sentence beside the button, where the decision is made.
           Shown every day now: someone ordering on a Sunday is told the next
           dispatch is Monday, which is the thing they actually want to know
           and was previously an empty space. */
        var rail = document.querySelector('[data-tcfr-cutoff]');
        if (rail) {
          rail.innerHTML = text;
          // Never over a sold-out size, where there is nothing to order.
          var add = document.querySelector('[data-tcfr-add]');
          rail.hidden = !!(add && add.disabled);
        }
      }
    }

    var list = document.createElement('ul');
    list.className = 'tcfr-delivery__list';
    options.forEach(function (o) {
      var li = document.createElement('li');
      var name = document.createElement('span');
      name.className = 'tcfr-delivery__name';
      name.textContent = o.name;
      li.appendChild(name);

      if (o.estimate) {
        var est = document.createElement('span');
        est.className = 'tcfr-delivery__est';
        est.textContent = o.estimate;
        li.appendChild(est);
      }

      var price = document.createElement('span');
      price.className = 'tcfr-delivery__price';
      price.textContent = priceLabel(o);
      li.appendChild(price);

      if (o.freeOver) {
        var free = document.createElement('span');
        free.className = 'tcfr-delivery__free';
        free.textContent = t('free_over', thresholdLabel(o.freeOver, o.currency));
        li.appendChild(free);
      }
      list.appendChild(li);
    });
    host.appendChild(list);
  }

  /**
   * The promise line under the buy panel, in the customer's country and words.
   *
   * The threshold comes from the same delivery data the accordion below is
   * built from, so the promise and the checkout cannot disagree; only the
   * wording lives here. A country with no free-delivery rate — Ireland, the
   * United States — gets the returns half on its own rather than a threshold
   * that does not exist.
   */
  function renderPromise(data) {
    var host = document.querySelector('[data-tcfr-promise]');
    if (!host) return;

    // The lowest threshold on offer, since any of them earns free delivery.
    var best = null;
    ((data && data.options) || []).forEach(function (o) {
      if (!o.freeOver) return;
      if (!best || parseFloat(o.freeOver) < parseFloat(best.freeOver)) best = o;
    });

    if (best) {
      host.textContent = t('promise_over', thresholdLabel(best.freeOver, best.currency));
    } else if ((data && data.options || []).length) {
      host.textContent = t('promise_returns');
    } else {
      // No rates at all means the request failed rather than that delivery is
      // free nowhere, so the merchant's own line is the safer thing to show.
      host.textContent = host.getAttribute('data-fallback') || '';
    }
  }

  /**
   * The made-to-order notice, and what it actually means.
   *
   * "Production time is 7-10 working days" is a true sentence answering the
   * wrong question. The customer reading it wants to know whether something has
   * gone wrong, whether the order is stuck and when they will hear from us —
   * which is what the (i) says, and why it says it above the button rather than
   * in an accordion below it.
   *
   * The line itself is a theme setting, so it is English wherever the merchant
   * typed it. Given the working days it takes, the other four storefronts get
   * the sentence written for them instead.
   */
  function setupNotice() {
    var host = document.querySelector('[data-tcfr-notice]');
    if (!host || host.__ready) return;
    host.__ready = true;

    var days = (host.getAttribute('data-days') || '').trim();
    // Hyphens are what gets typed; a range is set in type with an en dash.
    var pretty = days.replace(/\s*-\s*/g, '–');

    if (days && LOCALE !== 'en') {
      var line = host.querySelector('[data-tcfr-notice-text]');
      if (line) line.textContent = t('made_to_order', pretty);
    }

    var more = host.querySelector('[data-tcfr-notice-more]');
    var detail = host.querySelector('[data-tcfr-notice-detail]');
    if (!more || !detail) return;

    // Nothing to explain without the number, and a control that opens an empty
    // box is worse than no control.
    if (!days) {
      more.remove();
      return;
    }

    detail.textContent = t('made_to_order_detail', pretty);
    more.addEventListener('click', function () {
      var open = more.getAttribute('aria-expanded') === 'true';
      more.setAttribute('aria-expanded', open ? 'false' : 'true');
      detail.hidden = open;
    });
  }

  function loadDelivery() {
    var host = document.querySelector('[data-tcfr-delivery]');
    var endpoint = cfg().shippingEndpoint;
    // An empty promise line is worse than the merchant's own, so anything that
    // stops the fetch before it starts falls back rather than leaves a gap.
    if (!host || !endpoint) return renderPromise(null);
    if (host.__loaded) return;
    host.__loaded = true;

    fetch(endpoint + '?country=' + encodeURIComponent(host.getAttribute('data-country') || 'GB'), {
      credentials: 'omit',
    })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (data) {
        renderDelivery(host, data);
        renderPromise(data);
        // A minute is fine: nobody watches the seconds tick, and re-rendering
        // every second for a number that changes every sixty is waste.
        setInterval(function () {
          if (host.querySelector('[data-live]')) renderDelivery(host, data);
        }, 60000);
      })
      .catch(function () {
        host.__loaded = false;
        renderPromise(null);
      });
  }

  /**
   * App blocks that render nothing still take their place in the column.
   *
   * The buy column is a grid with a 20px gap, and three of its app blocks draw
   * nothing at all on most products — a hidden widget, a script or two. Each
   * still claimed a gap, so sixty pixels of nothing sat between the size
   * buttons and the button that matters.
   *
   * They cannot be matched with :empty — they hold whitespace and script tags —
   * and cannot be hidden once and forgotten, because an app may fill its block
   * a second later. So each is watched and hidden only while it is genuinely
   * drawing nothing.
   */
  /*
   * The size guide, against a label rather than adrift.
   *
   * On a suit sold as separates there is no size row, so the block falls back
   * to a button of its own — which on a phone left "Size guide" floating in a
   * band of white between the age swatches and Choose your sizes, attached to
   * nothing. The variant group's own head is the same kind of row the guide
   * sits in everywhere else: a label, its current value, and the question you
   * might ask about it. So it goes there.
   *
   * Watched rather than done once, because the button arrives after a fetch —
   * the same race that had the whole block hidden.
   */
  function homeTheSizeGuide() {
    var rail = document.querySelector('.tcfr__rail') || document;

    var place = function () {
      var button = rail.querySelector('.tc-size__button:not(.tc-size__button--inline)');
      var head = rail.querySelector('.tc-vg__head');
      if (!button || !head || button.__tcHomed) return;
      button.__tcHomed = true;
      button.classList.add('tc-size__button--vg');
      head.appendChild(button);
    };

    place();
    if (typeof MutationObserver === 'function') {
      new MutationObserver(place).observe(rail === document ? document.body : rail, {
        childList: true,
        subtree: true,
      });
    }
  }

  function collapseHollowBlocks() {
    var rail = document.querySelector('.tcfr__rail');
    if (!rail || rail.__tcHollow) return;
    rail.__tcHollow = true;

    var blocks = rail.querySelectorAll('.shopify-app-block');
    var judge = function (block) {
      if (block.parentElement !== rail) return;
      /* Measured with the class off, so a block that has just filled is not
         judged on the state we put it in. */
      block.classList.remove('is-hollow');
      if (block.getBoundingClientRect().height === 0) block.classList.add('is-hollow');
    };

    for (var i = 0; i < blocks.length; i++) {
      judge(blocks[i]);
      (function (block) {
        /*
         * Watched for content, not for size.
         *
         * A ResizeObserver alone deadlocks: hollow means display:none, and a
         * display:none element has no box to resize, so it never reports the
         * moment it fills and stays hidden for the life of the page. Whether
         * the block ever appeared came down to whether its app answered before
         * the first judgement — which the size guide, waiting on a fetch, lost
         * on slower connections while winning on fast ones. That reads as "it
         * works on desktop but not on my phone" and is really a race.
         *
         * A MutationObserver sees the button being appended whether the block
         * is displayed or not, and judge() takes the class off before it
         * measures, so there is a box to measure by then.
         */
        if (typeof MutationObserver === 'function') {
          new MutationObserver(function () { judge(block); })
            .observe(block, { childList: true, subtree: true });
        }
        if (typeof ResizeObserver === 'function') {
          new ResizeObserver(function () { judge(block); }).observe(block);
        }
      }(blocks[i]));
    }
  }

  /**
   * The reviews widget lays itself out in two columns: the reviews on the left,
   * the rating summary on the right. On a product nobody has reviewed yet the
   * right column has nothing to hold, so "No reviews yet, write one now?" sits
   * across two thirds of a card and the last third is a hole.
   *
   * Judged on the height of that column rather than on the wording, so it does
   * not break the day Okendo rewrites the sentence — or translates it.
   */
  function tidyEmptyReviews() {
    var wrap = document.querySelector('.tcfr-reviews__full');
    if (!wrap || wrap.__tcTidy) return;
    wrap.__tcTidy = true;

    var judge = function () {
      /* Wait for the widget itself; judging before it lands would collapse a
         product that does have reviews, for a moment. */
      var grid = wrap.querySelector('.js-oke-widgetSize');
      if (!grid) return;
      /* On an unreviewed product the summary column is not merely short — it is
         not rendered at all, which the first version treated as "nothing to
         judge" and walked away from. Absent is the emptiest it gets.

         Measured with the class off. Leaving it on hides the column, which
         makes its height zero, which keeps it hidden — a product with
         thirty-three reviews lost its ratings that way. */
      wrap.classList.remove('is-unreviewed');
      var summary = wrap.querySelector('.oke-w-header');
      var hollow = !summary || summary.getBoundingClientRect().height < 80;
      if (hollow) wrap.classList.add('is-unreviewed');
    };

    judge();
    if (typeof MutationObserver === 'function') {
      var watch = new MutationObserver(judge);
      watch.observe(wrap, { childList: true, subtree: true });
      /* The widget arrives well after this script; stop watching once it has
         settled rather than listening to the page for ever. */
      setTimeout(function () { watch.disconnect(); judge(); }, 15000);
    }
  }

  function boot() {
    document.querySelectorAll('[data-tcfr]').forEach(init);
    watchReviews();
    setupNotice();
    loadDelivery();
    collapseHollowBlocks();
    homeTheSizeGuide();
    tidyEmptyReviews();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-initialise after a section render (theme editor, or a section swap).
  document.addEventListener('shopify:section:load', boot);
})();
